import axiosInstance from '../utils/axios';

// 选项结构接口
export interface Option {
  id?: number;                       // 选项ID（可选）
  label: string;                     // 选项标签，如"A"、"B"、"C"
  value: string;                     // 选项值/显示内容
  order_num: number;                 // 选项排序号
  has_sub_options: boolean;          // 是否有子选项
  has_input: boolean;                // 是否包含额外输入框
  input_label: string;               // 输入框标签，如"请填写具体原因"
  has_multiple_inputs: boolean;      // 是否有多个输入框
  input_labels: string[];            // 多个输入框的标签数组
  sub_options?: Option[];            // 子选项数组（递归结构）
  parent_id?: number;                 // 父选项ID（可选）
}

export interface ShowWhen {
  question_id: string;
  value: string;
}

// 问题结构接口
export interface Question {
  id?: number;                       // 问题ID（可选，新增时为空）
  survey_id?: number;                // 所属问卷ID（可选）
  section: string;                   // 问题分组名称，如"基本信息"、"诊断信息"、"治疗方案"
  title: string;                     // 问题标题/内容
  type: 'text' | 'radio' | 'checkbox' | 'textarea' | 'file' | 'sort';  // 问题类型
  required: boolean;                 // 是否必填
  order_num?: number;                // 问题排序号（可选）
  parent_id?: number | null;         // 父问题ID，用于创建子问题/条件显示（可选）
  level: number;                     // 问题层级：0=根问题，1=子问题，2=孙问题...
  allow_other: boolean;              // 是否允许"其他"选项（单选、多选题适用）
  answered_by: 'user' | 'approver';  // 问题回答人：'user'=用户回答，'approver'=审批人回答
  show_when?: {                      // 条件显示配置（可选）
    question_id: number;             // 依赖的问题ID
    answer: string | string[];       // 触发显示的答案值（单个值或数组）
  };
  options: Option[];                 // 选项数组（单选题、多选题使用）
  file_types?: string[];             // 允许的文件类型，如['jpg','png','pdf']（文件上传题使用）
  max_size?: number;                 // 最大文件大小（KB）（文件上传题使用）
  created_at?: string;               // 创建时间（可选）
  updated_at?: string;               // 更新时间（可选）
}

export interface QuestionSection {
  section: string;
  questions: Question[];
}

export interface Survey {
  id?: number;
  title: string;
  description: string;
  type: string;
  status?: string;
  questions?: Question[];
  price?: number;
  createdBy?: number;
  updatedBy?: number;
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SurveyQuery {
  page?: number;
  page_size?: number;
  title?: string;
  type?: string;
  status?: string;
}

export interface StatusStat {
  status: string;
  count: number;
}

export interface SurveyStats {
  total_surveys: number;
  status_stats: StatusStat[];
}

export interface PaginatedResponse<T> {
  total: number;
  page: number;
  page_size: number;
  data: T[];
}

export interface SurveyService {
  // 获取问卷列表
  getSurveys: (params: SurveyQuery) => Promise<PaginatedResponse<Survey>>;

  // 获取问卷详情
  getSurvey: (id: number) => Promise<Survey>;

  // 创建问卷
  createSurvey: (survey: Partial<Survey>) => Promise<Survey>;

  // 更新问卷
  updateSurvey: (id: number, survey: Partial<Survey>) => Promise<Survey>;

  // 删除问卷
  deleteSurvey: (id: number) => Promise<{ success: boolean; message: string }>;

  // 发布问卷
  publishSurvey: (id: number) => Promise<Survey>;

  // 结束问卷
  endSurvey: (id: number) => Promise<Survey>;

  // 复制问卷
  copySurvey: (id: number, title?: string) => Promise<Survey>;

  // 获取问卷统计信息
  getStats: () => Promise<SurveyStats>;

  // 获取问题列表
  getQuestions: (survey_id: number) => Promise<Question[]>;

  // 创建问题
  createQuestion: (survey_id: number, question: Partial<Question>) => Promise<Question>;

  // 更新问题
  updateQuestion: (id: number, question: Partial<Question>) => Promise<Question>;

  // 删除问题
  deleteQuestion: (id: number) => Promise<{ success: boolean; message: string }>;

  // 批量删除问题
  batchDeleteQuestions: (questionIds: number[]) => Promise<{ success: boolean; message: string }>;

  // 更新问题顺序
  updateQuestionsOrder: (questions: { id: number; order_num: number }[]) => Promise<{ success: boolean; message: string }>;
}

export const surveyService: SurveyService = {
  // 获取问卷列表
  getSurveys: async (params: SurveyQuery) => {
    const response = await axiosInstance.get<PaginatedResponse<Survey>>('/surveys', { params });
    return response.data;
  },

  // 获取问卷详情
  getSurvey: async (id: number) => {
    const response = await axiosInstance.get<Survey>(`/surveys/${id}`);
    return response.data;
  },

  // 创建问卷
  createSurvey: async (survey: Partial<Survey>) => {
    const response = await axiosInstance.post<Survey>('/surveys', survey);
    return response.data;
  },

  // 更新问卷
  updateSurvey: async (id: number, survey: Partial<Survey>) => {
    const response = await axiosInstance.put<Survey>(`/surveys/${id}`, survey);
    return response.data;
  },

  // 删除问卷
  deleteSurvey: async (id: number) => {
    const response = await axiosInstance.delete(`/surveys/${id}`);
    return response.data;
  },

  // 发布问卷
  publishSurvey: async (id: number) => {
    const response = await axiosInstance.post<Survey>(`/surveys/${id}/publish`);
    return response.data;
  },

  // 结束问卷
  endSurvey: async (id: number) => {
    const response = await axiosInstance.post<Survey>(`/surveys/${id}/end`);
    return response.data;
  },

  // 复制问卷
  copySurvey: async (id: number, title?: string) => {
    const response = await axiosInstance.post<Survey>(`/surveys/${id}/copy`, { title });
    return response.data;
  },

  // 获取问卷统计信息
  getStats: async () => {
    const response = await axiosInstance.get<SurveyStats>('/surveys/stats');
    return response.data;
  },

  // 获取问题列表
  getQuestions: async (survey_id: number) => {
    const response = await axiosInstance.get<Question[]>(`/surveys/${survey_id}/questions`);
    return response.data;
  },

  // 创建问题
  createQuestion: async (survey_id: number, question: Partial<Question>) => {
    const response = await axiosInstance.post<Question>(`/surveys/${survey_id}/questions`, question);
    return response.data;
  },

  // 更新问题
  updateQuestion: async (id: number, question: Partial<Question>) => {
    const response = await axiosInstance.put<Question>(`/questions/${id}`, question);
    return response.data;
  },

  // 删除问题
  deleteQuestion: async (id: number) => {
    const response = await axiosInstance.delete(`/questions/${id}`);
    return response.data;
  },

  // 批量删除问题
  batchDeleteQuestions: async (questionIds: number[]) => {
    const response = await axiosInstance.post('/questions/batch-delete', { questionIds });
    return response.data;
  },

  // 更新问题顺序
  updateQuestionsOrder: async (questions: { id: number; order_num: number }[]) => {
    const response = await axiosInstance.put('/questions/order', { questions });
    return response.data;
  }
};